/* eslint-disable no-debugger */
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack5';
import { useEffect } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
// material
import { LoadingButton } from '@material-ui/lab';
import { Card, Grid, Stack, TextField, Autocomplete, Box } from '@material-ui/core';
// eslint-disable-next-line import/no-unresolved
import CaseConvertor from 'src/components/common-components/CaseConvertor';
// eslint-disable-next-line import/no-unresolved
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import {
  createProject,
  updateProject,
  resetCreateProjectStatus,
  resetProjectError
} from '../../../redux/slices/project';

// ----------------------------------------------------------------------

CreateProjectForm.propTypes = {
  editId: PropTypes.number,
  setVisibleComponent: PropTypes.func,
  currentProject: PropTypes.object,
  CLIENT_OPTION: PropTypes.array,
  VENDOR_OPTION: PropTypes.array
};

export default function CreateProjectForm({
  editId,
  setVisibleComponent,
  currentProject = {},
  CLIENT_OPTION,
  VENDOR_OPTION
}) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { error, createProjectStatus, isLoading } = useSelector((state) => state.project);

  useEffect(() => {
    if (error || createProjectStatus) {
      enqueueSnackbar(
        (createProjectStatus && (!editId ? 'New Project Created Successfully' : 'Project Updated Successfully')) ||
          (error && error),
        { variant: error ? 'error' : 'success' }
      );
      dispatch(resetProjectError());
    }
    if (createProjectStatus) {
      setVisibleComponent('List');
      formik.resetForm();
      dispatch(resetCreateProjectStatus());
    }
  }, [error, createProjectStatus]);

  const NewProjectSchema = Yup.object().shape({
    projectName: Yup.string().required('Project name is required'),
    clientName: Yup.mixed().required('Client is required'),
    vendorName: Yup.mixed().required('vendor is required')
  });

  const editTempData = {
    editProjectName: currentProject.project_name ? currentProject.project_name : '',
    editVendorName: currentProject.vendor ? VENDOR_OPTION.filter((a) => a.id === currentProject.vendor) : '',
    editClientName: currentProject.client ? CLIENT_OPTION.filter((a) => a.id === currentProject.client) : ''
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      projectName: editId ? editTempData.editProjectName : '',
      vendorName: editId ? editTempData.editVendorName[0] : '',
      clientName: editId ? editTempData.editClientName[0] : ''
    },
    validationSchema: NewProjectSchema,
    onSubmit: async (values, { setSubmitting, resetForm, setErrors }) => {
      try {
        const projectData = {
          project_name: CaseConvertor(values.projectName),
          vendor: values.vendorName.id,
          client: values.clientName.id
        };
        dispatch(!editId ? createProject(projectData) : updateProject(projectData, editId));
        setSubmitting(false);
      } catch (error) {
        console.error(error);
        setErrors(error);
      }
    }
  });

  const { errors, values, touched, handleSubmit, isSubmitting, setFieldValue, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Card sx={{ p: 3, overflow: 'visible' }}>
              <Stack spacing={3}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <TextField
                    fullWidth
                    label="Project Name"
                    required
                    {...getFieldProps('projectName')}
                    error={Boolean(touched.projectName && errors.projectName)}
                    helperText={touched.projectName && errors.projectName}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <Autocomplete
                    fullWidth
                    value={values?.vendorName ? values?.vendorName : null}
                    name="vendorName"
                    options={VENDOR_OPTION}
                    onChange={(e, value) => setFieldValue('vendorName', value)}
                    getOptionLabel={(option) => (option ? option.vendor_name : '')}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        error={Boolean(touched.vendorName && errors.vendorName)}
                        helperText={touched.vendorName && errors.vendorName}
                        required
                        {...params}
                        label="Vendor"
                        margin="none"
                      />
                    )}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                  <Autocomplete
                    fullWidth
                    name="clientName"
                    value={values?.clientName ? values?.clientName : null}
                    options={CLIENT_OPTION}
                    onChange={(e, value) => setFieldValue('clientName', value)}
                    getOptionLabel={(option) => (option ? option.client_name : '')}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        error={Boolean(touched.clientName && errors.clientName)}
                        helperText={touched.clientName && errors.clientName}
                        required
                        {...params}
                        label="Client"
                        margin="none"
                      />
                    )}
                  />
                </Stack>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <LoadingButton type="submit" variant="contained" loading={isLoading}>
                    {!editId ? 'Save' : 'Save Changes'}
                  </LoadingButton>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  );
}
